import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, CornerDownLeft, FileText, FolderKanban, Building2, User as UserIcon } from 'lucide-react';
import { useClientStore } from '../../../lib/stores/clientStore';
import { useDealStore } from '../../../lib/stores/dealStore';
import { supabase } from '../../../lib/supabase';
import type { Client } from '../../../lib/types';
import { KeyHint } from './DrawerControls';

export type LinkTarget = 'client' | 'deal' | 'project';

export interface SearchRow {
  id: string;
  primary: string;
  secondary?: string;
  clientId?: string | null;
  /** Solo per le schede anagrafiche: serve a capire se il risultato e` un'azienda. */
  entityType?: string | null;
  /** Email della scheda, per riconoscerla come interna e per l'aggiornamento immediato. */
  email?: string | null;
}

interface Props {
  target: LinkTarget;
  /** Mostra la scorciatoia "Crearne uno?" quando la ricerca non trova nulla. */
  canCreate: boolean;
  linking: boolean;
  onSelect: (row: SearchRow) => void;
  onSwitchToCreate: () => void;
}

const PLACEHOLDERS: Record<LinkTarget, string> = {
  client: 'Cerca per nome, email o P.IVA...',
  deal: 'Cerca offerta per titolo o cliente...',
  project: 'Cerca progetto per nome...',
};

const EMPTY_HINT: Record<LinkTarget, string> = {
  client: 'Digita almeno due lettere del nome, della email o della partita IVA.',
  deal: "Digita almeno due lettere del titolo dell'offerta o del cliente.",
  project: 'Digita almeno due lettere del nome del progetto.',
};

function RowIcon({ target, entityType }: { target: LinkTarget; entityType?: string | null }) {
  if (target === 'deal') return <FileText className="h-4 w-4 text-[#787774]" />;
  if (target === 'project') return <FolderKanban className="h-4 w-4 text-[#787774]" />;
  return entityType === 'company'
    ? <Building2 className="h-4 w-4 text-[#787774]" />
    : <UserIcon className="h-4 w-4 text-[#787774]" />;
}

function Skeleton() {
  return (
    <div className="space-y-1" aria-hidden>
      {[0, 1, 2].map(i => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <div className="mail-link-skeleton w-8 h-8 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="mail-link-skeleton h-3 rounded" style={{ width: `${70 - i * 12}%` }} />
            <div className="mail-link-skeleton h-2.5 rounded" style={{ width: `${45 - i * 8}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Ricerca dell'entita' a cui collegare il thread. */
export function ExistingSearchPanel({ target, canCreate, linking, onSelect, onSwitchToCreate }: Props) {
  const searchClientsAndLeads = useClientStore(s => s.searchClientsAndLeads);
  const searchDealsGlobal = useDealStore(s => s.searchDealsGlobal);

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Azzera la ricerca quando cambia la destinazione.
  useEffect(() => { setSearchTerm(''); setResults([]); setHighlight(0); }, [target]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      let rows: SearchRow[] = [];
      if (target === 'client') {
        const found = await searchClientsAndLeads(term);
        rows = found.map((c: Client) => ({
          id: c.id,
          primary: c.business_name || (c as { trade_name?: string }).trade_name || '—',
          secondary: c.email || undefined,
          clientId: c.id,
          entityType: (c as { entity_type?: string }).entity_type ?? null,
          email: c.email ?? null,
        }));
      } else if (target === 'deal') {
        const found = await searchDealsGlobal(term, 30);
        rows = found.map(d => ({
          id: d.id,
          primary: d.title || 'Offerta senza titolo',
          secondary: d.client_business_name || undefined,
          clientId: d.client_id,
        }));
      } else {
        const escaped = term.replace(/[%_]/g, '\\$&');
        const { data } = await supabase
          .from('projects')
          .select('id, name, status, client_id')
          .ilike('name', `%${escaped}%`)
          .limit(30);
        rows = (data || []).map((p: { id: string; name: string; status: string; client_id: string | null }) => ({
          id: p.id, primary: p.name, secondary: p.status, clientId: p.client_id,
        }));
      }
      setResults(rows);
      setHighlight(0);
      setSearching(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm, target, searchClientsAndLeads, searchDealsGlobal]);

  // La riga evidenziata resta sempre in vista quando ci si muove con le frecce.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0 || linking) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = results[highlight];
      if (row) onSelect(row);
    }
  };

  const showEmptyHint = !searching && searchTerm.trim().length < 2;
  const showNoResults = !searching && searchTerm.trim().length >= 2 && results.length === 0;

  return (
    <div className="space-y-3 mail-link-section">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787774] pointer-events-none" />
        <input
          autoFocus
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={PLACEHOLDERS[target]}
          className="w-full pl-9 pr-9 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-transparent rounded-lg transition-all duration-150 focus:outline-none focus:bg-white dark:focus:bg-gray-800 focus:border-[#2383E2] focus:ring-2 focus:ring-[#2383E2]/15 text-[#37352F] dark:text-white placeholder:text-[#9B9A97]"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-[#787774]" />
        )}
      </div>

      {showEmptyHint && (
        <div className="px-1 py-8 text-center mail-link-section">
          <Search className="h-7 w-7 mx-auto mb-2.5 text-[#D3D2D0] dark:text-white/15" />
          <p className="text-xs text-[#787774] dark:text-white/40 max-w-[260px] mx-auto leading-relaxed">
            {EMPTY_HINT[target]}
          </p>
        </div>
      )}

      {searching && results.length === 0 && <Skeleton />}

      {showNoResults && (
        <div className="px-1 py-8 text-center mail-link-section">
          <p className="text-sm text-[#37352F] dark:text-white/80 mb-1">Nessun risultato</p>
          <p className="text-xs text-[#787774] dark:text-white/40">
            {canCreate
              ? <>Non c'e' ancora in anagrafica. <button onClick={onSwitchToCreate} className="text-[#2383E2] hover:underline font-medium">Crearla ora?</button></>
              : 'Prova con un altro termine di ricerca.'}
          </p>
        </div>
      )}

      {results.length > 0 && (
        <>
          <div ref={listRef} className="space-y-0.5">
            {results.map((row, i) => (
              <button
                key={row.id}
                data-index={i}
                disabled={linking}
                onClick={() => onSelect(row)}
                onMouseEnter={() => setHighlight(i)}
                className={`mail-link-row group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-100 text-left disabled:opacity-50 ${
                  highlight === i ? 'bg-[rgba(55,53,47,0.05)] dark:bg-white/[0.07]' : ''
                }`}
                style={{ animationDelay: `${Math.min(i, 8) * 18}ms` }}
              >
                <div className="w-8 h-8 rounded-full bg-[rgba(55,53,47,0.06)] dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                  <RowIcon target={target} entityType={row.entityType} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">{row.primary}</p>
                  {row.secondary && <p className="text-xs text-[#787774] dark:text-white/40 truncate">{row.secondary}</p>}
                </div>
                {linking && highlight === i
                  ? <Loader2 className="h-4 w-4 animate-spin text-[#787774] flex-shrink-0" />
                  : (
                    <span className={`flex-shrink-0 transition-opacity duration-100 ${highlight === i ? 'opacity-100' : 'opacity-0'}`}>
                      <CornerDownLeft className="h-3.5 w-3.5 text-[#787774]" />
                    </span>
                  )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-1 text-[11px] text-[#9B9A97] dark:text-white/30">
            <span className="inline-flex items-center gap-1"><KeyHint>↑</KeyHint><KeyHint>↓</KeyHint> naviga</span>
            <span className="inline-flex items-center gap-1"><KeyHint>↵</KeyHint> collega</span>
          </div>
        </>
      )}
    </div>
  );
}
